import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarMenus from '../components/SidebarMenus';
import api from '../utils/api';
import SweetAlert from '../components/SweetAlert';
import '../assets/styles/dashboard.css';
import '../assets/styles/create-booking.css';

import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Header from '../components/Header';

//const EXTRAS = ['Luggage', 'XL Luggage', 'Baby Seat', 'Wheelchair'];
//const TRIP_TYPES = ['Immediate', 'Scheduled', 'Airport'];
const TRIP_TYPES = ['Immediate', 'Scheduled', 'Airport'];
const COMMISSION_RATE = 0;

const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').replace(
  /^['"]|['"]$/g,
  ''
);

const CURRENCY_SYMBOL = process.env.REACT_APP_CURRENCY_SYMBOL || 'kr';
const TIMEZONE = process.env.REACT_APP_TIMEZONE_RESION || 'Atlantic/Reykjavik';

function getNowInTimezone(timeZone = TIMEZONE) {
  // en-CA → YYYY-MM-DD, en-GB 24h → HH:mm
  const date = new Date().toLocaleDateString('en-CA', { timeZone });
  const time = new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  });
  return `${date}T${time}`; // e.g. "2026-07-20T07:05"
}




function isNight(date = new Date()) {
  const h = date.getHours();
  return h >= 17 || h < 8; // 5pm–8am
}

function calcVehicleFare(vehicle, distanceKm, when = new Date()) {
  if (!distanceKm || distanceKm <= 0) return null;

  const night = isNight(when);
  const tiers = [...(vehicle.kilometerPricing || [])].sort(
    (a, b) => a.kilometer - b.kilometer
  );



  let remaining = distanceKm;
  //let fare = Number(vehicle.startingFees) || 0;
  let fare = 0;
  let originalPrice = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const used = Math.min(remaining, tier.kilometer);
    const rate = night ? tier.nightPrice : tier.dayPrice;
    const offer = night ? tier.nightOffer : tier.dayOffer;
    const offerType = night ? tier.nightOfferType : tier.dayOfferType;

    let segment = used * rate;
    originalPrice += segment;


    if (offer && offerType === 'percent') {
      segment -= (segment * offer) / 100;
    }
    if (offer && offerType === 'flat') {
      segment -= offer;
    }

    fare += segment;
    remaining -= used;
  }

  if (remaining > 0) {
    const fallback = night ? vehicle.nightPrice : vehicle.price;
    fare += remaining * (fallback || 0);
    originalPrice += remaining * (fallback || 0);
  }

  //fare += Number(vehicle.adminFee) || 0;

  //return Math.max(0, Number(fare.toFixed(2)));
  return {
    originalPrice: originalPrice,
    estimatedFare: Math.max(0, Number(fare.toFixed(2))),
  };
}

function loadGoogleMaps(apiKey) {
  if (!apiKey) return Promise.reject(new Error('Missing Google Maps API key'));
  if (window.google?.maps?.places) return Promise.resolve();

  if (window.__googleMapsPromise) return window.__googleMapsPromise;

  window.__googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

  return window.__googleMapsPromise;
}

const CreateBooking = () => {
  const navigate = useNavigate();
  const agencyData = JSON.parse(localStorage.getItem('agencyData') || '{}');
  const partnerName = agencyData.companyName || 'Partner';
  const partnerEmail = agencyData.email || '';
  const agencyId = agencyData._id || '';
  const initials = partnerName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupAutocompleteRef = useRef(null);
  const dropoffAutocompleteRef = useRef(null);

  const [pickup, setPickup] = useState(null); // { address, lat, lng }
  const [dropoff, setDropoff] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null);
  const [encodedPolyline, setEncodedPolyline] = useState('');
  const [mapsReady, setMapsReady] = useState(false);

  const maxSmallBagCount = 50;
  const maxMediumBagCount = 30;
  const maxLargeBagCount = 20;

  //  const staticPickupAddress = 'Bestech Business Tower';
  //  const staticFromLat = 30.6752;
  //  const staticFromLng = 76.7409;
  //  const staticToAddress = 'Sohana gurudwara, 229, sector 78, sahibzada ajit singh nagar, Punjab 140308, india';
  //  const staticToLat = 30.6903;
  //  const staticToLng = 76.7102;


  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    tripType: 'Immediate',
    vehicleType: '',
    passengers: 1,
    smallBagCount: 0,
    mediumBagCount: 0,
    largeBagCount: 0,
    //extras: [],
    specialRequests: '',
    scheduledAt: '',
    flightNumber: '',
  });

  const [flightDetails, setFlightDetails] = useState({
    airlineName: '',
    flightNumber: '',
    airportName: '',
    terminal: '',
    arrivalDateTime: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [searchingFlight, setSearchingFlight] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const fareTime =
    form.tripType === 'Scheduled' && form.scheduledAt
      ? new Date(form.scheduledAt)
      : new Date();

  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/');
  }, [navigate]);



  useEffect(() => {
    const getVehicles = async () => {
      try {
        const response = await api.post('/api/website/get-vehicles-type');
        if (response.data.status === 1) {
          const list = response.data.vehiclesType || [];
          setVehicleTypes(list);
          if (list.length > 0) {
            setForm((prev) =>
              prev.vehicleType ? prev : { ...prev, vehicleType: list[0]._id }
            );
          }
        } else {
          SweetAlert.error('Error', response.data.message || 'Something went wrong.');
        }
      } catch (err) {
        SweetAlert.error(
          'Error',
          err.response?.data?.message || 'Failed to load vehicle types.'
        );
      }
    };
    getVehicles();
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (!cancelled) setMapsReady(true);
      })
      .catch((err) => {
        console.error(err);
        SweetAlert.error(
          'Maps Error',
          'Could not load Google Places. Check REACT_APP_GOOGLE_MAPS_API_KEY.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mapsReady || !window.google?.maps?.places) return;
    if (!pickupInputRef.current || !dropoffInputRef.current) return;

    if (!pickupAutocompleteRef.current) {
      const ac = new window.google.maps.places.Autocomplete(pickupInputRef.current, {
        fields: ['formatted_address', 'geometry', 'name'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place.geometry?.location) return;
        setPickup({
          address: place.formatted_address || place.name || '',
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
        setErrors((prev) => ({ ...prev, pickupAddress: '' }));
      });
      pickupAutocompleteRef.current = ac;
      // setPickup({
      //   address: staticPickupAddress,
      //   lat: staticFromLat,
      //   lng: staticFromLng,
      // });
    }

    if (!dropoffAutocompleteRef.current) {
      const ac = new window.google.maps.places.Autocomplete(dropoffInputRef.current, {
        fields: ['formatted_address', 'geometry', 'name'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place.geometry?.location) return;
        setDropoff({
          address: place.formatted_address || place.name || '',
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
        setErrors((prev) => ({ ...prev, dropoffAddress: '' }));
      });
      dropoffAutocompleteRef.current = ac;
      // setDropoff({
      //   address: staticToAddress,
      //   lat: staticToLat,
      //   lng: staticToLng,
      // });
    }
  }, [mapsReady]);

  useEffect(() => {
    if (!pickup?.lat || !dropoff?.lat || !window.google?.maps?.DirectionsService) {
      setDistanceKm(null);
      setEncodedPolyline('');
      return;
    }
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: { lat: pickup.lat, lng: pickup.lng },
        destination: { lat: dropoff.lat, lng: dropoff.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status !== 'OK' || !result?.routes?.[0]) {
          setDistanceKm(null);
          setEncodedPolyline('');
          return;
        }
        const route = result.routes[0];
        const meters = route.legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0);
        setDistanceKm(meters / 1000);
        // JS API: overview_polyline is usually { points: "..." }
        const polyline =
          typeof route.overview_polyline === 'string'
            ? route.overview_polyline
            : route.overview_polyline?.points || '';
        setEncodedPolyline(polyline);
      }
    );
  }, [pickup, dropoff]);

  const handleFlightSearch = async () => {
    if (!form.flightNumber?.trim()) {
      setErrors((prev) => ({
        ...prev,
        flightNumber: 'Flight number is required',
      }));
      return;
    }

    try {
      setSearchingFlight(true);

      // Change endpoint/body to match your backend
      const res = await api.post('/api/website/getFlightDetails', {
        flightNumber: form.flightNumber.trim(),
        agencyId: agencyId,
      });

      if (res.data.status !== 1) {
        SweetAlert.error('Error', res.data.message || 'Flight not found.');
        return;
      }

      const data = res.data.data.response || {};

      // Prefill schedule + airport aside fields
      setFlightDetails({
        airlineName: data.airline_name || '',
        flightNumber: data.flight_number || '',
        airportName: data.arr_name || '',
        terminal: data.arr_terminal || '',
        arrivalDateTime: data.arr_time || '',
      });
      // scheduledAt must be: "YYYY-MM-DDTHH:mm"
      setForm((prev) => ({
        ...prev,
        scheduledAt: data.arr_time ? data.arr_time.replace(' ', 'T') : '',
        airportCode: data.arr_iata || '',

      }));

      //call api pass airport code and get airport name
      const airportRes = await api.post(`/api/website/getAirportDetails`, {
        airportCode: data.arr_iata,
        agencyId: agencyId,
      });
      //console.log('airportRes', airportRes);
      if (airportRes.data.status === 1) {
        setPickup({
          address: data.arr_name,
          lat: airportRes.data.data.latitude,
          lng: airportRes.data.data.longitude,
        });
        // required — Autocomplete input is uncontrolled
        if (pickupInputRef.current) {
          pickupInputRef.current.value = data.arr_name;
        }

        setErrors((prev) => ({ ...prev, pickupAddress: '', scheduledAt: '' }));
      }
      else {
        // Prefill pickup (still editable)
        if (data.arr_name && window.google?.maps?.places) {
          const attribution = document.createElement('div');
          const service = new window.google.maps.places.PlacesService(attribution);

          service.findPlaceFromQuery(
            {
              query: data.arr_name,
              fields: ['formatted_address', 'geometry', 'name'],
            },
            (results, status) => {
              if (
                status !== window.google.maps.places.PlacesServiceStatus.OK ||
                !results?.[0]?.geometry?.location
              ) {
                // fallback: address only, no lat/lng
                setPickup({ address: data.arr_name, lat: null, lng: null });
                return;
              }

              const place = results[0];
              const address = place.formatted_address || place.name || data.arr_name;

              setPickup({
                address: address,
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              });
              // required — Autocomplete input is uncontrolled
              if (pickupInputRef.current) {
                pickupInputRef.current.value = address;
              }
            }
          );

          setErrors((prev) => ({ ...prev, pickupAddress: '', scheduledAt: '' }));
        }

      }



      //SweetAlert.success('Success', 'Flight details loaded.');
    } catch (err) {
      console.log('err', err);
      // SweetAlert.error(
      //   'Error',
      //   err.response?.data?.message || 'Failed to fetch flight details.'
      // );
    } finally {
      setSearchingFlight(false);
    }
  };

  const selectedVehicle =
    vehicleTypes.find((v) => v._id === form.vehicleType) || vehicleTypes[0] || null;
  // const estimatedFare = selectedVehicle
  //   ? calcVehicleFare(selectedVehicle, distanceKm, fareTime). ?? 0
  //   : 0;
  const { originalPrice, estimatedFare } = selectedVehicle
    ? calcVehicleFare(selectedVehicle, distanceKm, fareTime) ?? { originalPrice: 0, estimatedFare: 0 }
    : { originalPrice: 0, estimatedFare: 0 };

  const commission = estimatedFare * COMMISSION_RATE;

  const summary = useMemo(
    () => ({
      vehicle: selectedVehicle?.vehicleName ?? '',
      passengers: form.passengers,
      tripType: form.tripType,
      originalPrice: originalPrice,
      fare: estimatedFare,
      commission,
      total: estimatedFare,
      distanceKm,
    }),
    [
      selectedVehicle,
      form.passengers,
      form.tripType,
      originalPrice,
      estimatedFare,
      commission,
      distanceKm,
    ]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // const toggleExtra = (extra) => {
  //   setForm((prev) => ({
  //     ...prev,
  //     extras: prev.extras.includes(extra)
  //       ? prev.extras.filter((x) => x !== extra)
  //       : [...prev.extras, extra],
  //   }));
  // };

  const setPassengers = (next) => {
    const maxPassengers = selectedVehicle?.numberOfPerson || 1;
    setForm((prev) => ({
      ...prev,
      passengers: Math.min(maxPassengers, Math.max(1, next)),
    }));
  };

  const setSmallBagCount = (next) => {
    setForm((prev) => ({
      ...prev,
      smallBagCount: Math.min(maxSmallBagCount, Math.max(0, next)),
    }));
  };
  const setMediumBagCount = (next) => {
    setForm((prev) => ({
      ...prev,
      mediumBagCount: Math.min(maxMediumBagCount, Math.max(0, next)),
    }));
  };
  const setLargeBagCount = (next) => {
    setForm((prev) => ({
      ...prev,
      largeBagCount: Math.min(maxLargeBagCount, Math.max(0, next)),
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = 'First name is required';
    if (!form.lastName.trim()) next.lastName = 'Last name is required';
    if (!form.phone) next.phone = 'Phone number is required';
    if (!form.email) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Please enter a valid email';
    else if (!isValidPhoneNumber(form.phone)) next.phone = 'Please enter a valid phone number';
    if (!pickup?.address) next.pickupAddress = 'Pickup address is required';
    if (!dropoff?.address) next.dropoffAddress = 'Dropoff address is required';
    if (form.tripType === 'Airport' && !form.flightNumber) next.flightNumber = 'Flight number is required';
    if ((form.tripType === 'Scheduled' || form.tripType === 'Airport') && !form.scheduledAt) {
      next.scheduledAt = 'Schedule date/time is required';
    }
    //if passengers is greater than the number of passengers in the vehicle, return error
    if (form.passengers > selectedVehicle?.numberOfPerson) {
      next.passengers = 'Passengers limit reached';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const bookingNow = getNowInTimezone(); // already in REACT_APP_TIMEZONE_RESION
    const [ridebooking_date, ridebooking_time] =
      form.tripType === 'Scheduled' || form.tripType === 'Airport'
        ? form.scheduledAt.split('T')
        : bookingNow.split('T');


    console.log('form', form);

    

    const payload = {
      agencyId: agencyId,
      from_address: pickup.address,
      to_address: dropoff.address,
      customerName: `${form.firstName.trim()} ${form.lastName.trim()}`,
      phone: form.phone.trim(),
      tripType: form.tripType,

       from_latitude: pickup.lat,
       from_longitude: pickup.lng,
     // from_latitude: staticFromLat,
      //from_longitude: staticFromLng,

       to_latitude: dropoff.lat,
       to_longitude: dropoff.lng,
      //to_latitude: staticToLat,
      //to_longitude: staticToLng,
      totalKM: distanceKm,
      vehicle_id: form.vehicleType || selectedVehicle?._id,
      vehicleType: selectedVehicle?.vehicleName,
      passengers: form.passengers,

      scheduledAt: form.tripType === 'Scheduled' || form.tripType === 'Airport' ? form.scheduledAt : null,
      price: summary.fare,
      originalPrice: summary.originalPrice,
      commission: summary.commission,
      currencySymbol: CURRENCY_SYMBOL,
      stops: [],
      splits: [],
      ridebooking_date: ridebooking_date,
      ridebooking_time: ridebooking_time,
      rideType: "agency",
      customerData: [{
        name: `${form.firstName.trim()} ${form.lastName.trim()}`,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      }],
      isScheduledRide: form.tripType === 'Scheduled' || form.tripType === 'Airport' ? 1 : 0,
      shareuserId: [],
      shareRideData: [],
      eventId: "",
      isAirportRide: form.tripType === 'Airport' ? 1 : 0,
      airPortRideData: {
        numberOfPerson: `${form.passengers}`,
        smallBags: form.smallBagCount,
        mediumBags: form.mediumBagCount,
        largeBags: form.largeBagCount,
        totalBags: form.smallBagCount + form.mediumBagCount + form.largeBagCount,
        specialNotes: form.specialRequests.trim(),
        airportCode: form.airportCode,
        terminal: flightDetails.terminal,
        flightNumber: flightDetails.flightNumber,
      },
      encodedPolyline
    };

    console.log('Booking payload:', payload);

    setSubmitting(true);
    try {
      //SweetAlert.success('Booking Created!', 'Your booking has been submitted.');

      const res = await api.post('/api/website/addRideAgency', payload);
      if (res.data.status === 1) {
        SweetAlert.success('Booking Created!', 'Your booking has been submitted.');
        navigate('/booking-history');
      } else {
        SweetAlert.error('Booking Failed!', res.data.message);
      }


    } catch (err) {
      SweetAlert.error(
        'Booking Failed!',
        err.response?.data?.message || 'Something went wrong.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate('/dashboard');


  return (
    <div className="dashboard-layout">
      <SidebarMenus partnerName={partnerName} partnerEmail={partnerEmail} />

      <main className="dashboard-main create-booking-main">
        <Header title="Create Booking" search={false} bell={true} partnerName={partnerName} initials={initials} searchPlaceholder="Search bookings, customers..." />

        <form className="create-booking-grid" onSubmit={handleSubmit}>
          <div className="create-booking-form">
            <section className="cb-card">
              <h3>Customer Information</h3>
              <div className="cb-row-2">
                <div className="cb-field">
                  <label>
                    First Name <span className="req">*</span>
                  </label>
                  <div className="cb-input-wrap">
                    <span className="cb-input-icon">👤</span>
                    <input
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="James Harrington"
                    />
                  </div>
                  {errors.firstName && (
                    <small className="text-danger">{errors.firstName}</small>
                  )}
                </div>

                <div className="cb-field">
                  <label>
                    Last Name <span className="req">*</span>
                  </label>
                  <div className="cb-input-wrap">
                    <span className="cb-input-icon">👤</span>
                    <input
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="James Harrington"
                    />
                  </div>
                  {errors.lastName && (
                    <small className="text-danger">{errors.lastName}</small>
                  )}
                </div>
                <div className="cb-field">
                  <label>
                    Phone Number <span className="req">*</span>
                  </label>
                  <div className="cb-input-wrap">

                    <PhoneInput
                      international
                      defaultCountry="IS"
                      countries={["IS"]}
                      addInternationalOption={false}
                      value={form.phone}
                      onChange={(value) => {
                        setForm((prev) => ({ ...prev, phone: value || '' }));
                        if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
                      }}
                      placeholder="+1 212-555-0142"
                    />

                  </div>
                  {errors.phone && (
                    <small className="text-danger">{errors.phone}</small>
                  )}
                </div>

                {/* for email */}
                <div className="cb-field">
                  <label>
                    Email <span className="req">*</span>
                  </label>
                  <div className="cb-input-wrap">
                    <input name="email" value={form.email} onChange={handleChange} placeholder="james@example.com" />
                  </div>
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </div>

              </div>

            </section>

            <section className="cb-card">
              <h3>Trip Type</h3>
              <div className="trip-type-row">
                {TRIP_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`trip-type-btn${form.tripType === type ? ' active' : ''}`}
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        tripType: type,
                        scheduledAt:
                          type === 'Scheduled' && !p.scheduledAt
                            ? getNowInTimezone()
                            : p.scheduledAt,
                      }))
                    }
                  >
                    {type}
                  </button>
                ))}
              </div>
              {form.tripType === 'Airport' && (
                <div className="cb-field" style={{ marginTop: 14 }}>
                  <label>
                    Flight Number <span className="req">*</span>
                  </label>
                  <div className="cb-input-wrap">
                    <input
                      type="text"
                      name="flightNumber"
                      value={form.flightNumber || ''}
                      onChange={handleChange}
                      placeholder="AI4216"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleFlightSearch();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleFlightSearch}
                      disabled={searchingFlight}
                      style={{
                        border: 'none',
                        background: '#2563eb',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '8px 14px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: searchingFlight ? 'not-allowed' : 'pointer',
                        opacity: searchingFlight ? 0.65 : 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {searchingFlight ? '...' : 'Search'}
                    </button>
                  </div>
                  {errors.flightNumber && (
                    <small className="text-danger">{errors.flightNumber}</small>
                  )}
                </div>



              )}
              {(form.tripType === 'Scheduled' || form.tripType === 'Airport') && (() => {
                const nowLocal = getNowInTimezone();
                const [minDate, minTime] = nowLocal.split('T');
                const [scheduledDate = '', scheduledTime = ''] = (form.scheduledAt || '').split('T');
                const timeMin = scheduledDate && scheduledDate === minDate ? minTime : undefined;

                const handleSchedulePartChange = (part, value) => {
                  const nextDate = part === 'date' ? value : scheduledDate;
                  const nextTime = part === 'time' ? value : scheduledTime;
                  const nextScheduledAt =
                    nextDate && nextTime ? `${nextDate}T${nextTime}` : nextDate ? `${nextDate}T` : '';

                  setForm((prev) => ({ ...prev, scheduledAt: nextScheduledAt }));
                  if (errors.scheduledAt) {
                    setErrors((prev) => ({ ...prev, scheduledAt: '' }));
                  }
                };

                return (
                  <div className="cb-row-2" style={{ marginTop: 14 }}>
                    <div className="cb-field">
                      <label>
                        Pickup Date <span className="req">*</span>
                      </label>
                      <input
                        type="date"
                        value={scheduledDate}
                        min={minDate}
                        onChange={(e) => handleSchedulePartChange('date', e.target.value)}
                        className="cb-plain-input"
                      />
                    </div>
                    <div className="cb-field">
                      <label>
                        Pickup Time <span className="req">*</span>
                      </label>
                      <input
                        type="time"
                        value={scheduledTime}
                        min={timeMin}
                        onChange={(e) => handleSchedulePartChange('time', e.target.value)}
                        className="cb-plain-input"
                      />
                      <img
                        src={require('../assets/images/clock.png')}
                        alt=""
                        className="clock"
                        aria-hidden="true"
                      />


                    </div>
                    {errors.scheduledAt && (
                      <small className="text-danger" style={{ gridColumn: '1 / -1' }}>
                        {errors.scheduledAt}
                      </small>
                    )}
                  </div>
                );
              })()}


            </section>

            <section className="cb-card">
              <h3>Trip Details</h3>
              <div className="cb-row-2">
                <div className="cb-field">
                  <label>
                    Pickup Address <span className="req">*</span>
                  </label>
                  <div className="cb-input-wrap">
                    <span className="cb-input-icon">📍</span>
                    <input
                      ref={pickupInputRef}
                      defaultValue=""
                      placeholder="Search pickup location"
                      autoComplete="off"
                    />
                    <img
                      src={require('../assets/images/search.png')}
                      alt="24x7 search"
                      className="search"
                      style={{ width: '16px', height: '16px', }}
                    />
                  </div>
                  {errors.pickupAddress && (
                    <small className="text-danger">{errors.pickupAddress}</small>
                  )}
                </div>
                <div className="cb-field">
                  <label>
                    Dropoff Address <span className="req">*</span>
                  </label>
                  <div className="cb-input-wrap">
                    <span className="cb-input-icon">✈</span>
                    <input
                      ref={dropoffInputRef}
                      defaultValue=""
                      placeholder="Search dropoff location"
                      autoComplete="off"
                    />
                    <img
                      src={require('../assets/images/search.png')}
                      alt="24x7 search"
                      className="search"
                      style={{ width: '16px', height: '16px', }}
                    />
                  </div>
                  {errors.dropoffAddress && (
                    <small className="text-danger">{errors.dropoffAddress}</small>
                  )}
                </div>
              </div>
              {distanceKm != null && (
                <p style={{ marginTop: 12, color: '#666', fontSize: 14 }}>
                  Distance: {distanceKm.toFixed(2)} km
                </p>
              )}
            </section>

            <section className="cb-card">
              <h3>Vehicle Type</h3>
              <div className="vehicle-grid">
                {vehicleTypes.length > 0 &&
                  vehicleTypes.map((v) => {
                    //const fare = calcVehicleFare(v, distanceKm, fareTime);
                    const { originalPrice, estimatedFare } = calcVehicleFare(v, distanceKm, fareTime) ?? { originalPrice: 0, estimatedFare: 0 };
                    return (
                      <button
                        key={v._id}
                        type="button"
                        className={`vehicle-card${form.vehicleType === v._id ? ' active' : ''
                          }`}
                        onClick={() =>
                          setForm((p) => ({ ...p, vehicleType: v._id }))
                        }
                      >
                        {form.vehicleType === v._id && (
                          <span className="vehicle-check">✓</span>
                        )}
                        <div className="vehicle-label">{v.vehicleName}</div>
                        <div className="vehicle-desc">
                          up to {v.numberOfPerson}{' '}
                          {v.numberOfPerson > 1 ? 'passengers' : 'passenger'}
                        </div>
                        <div className="vehicle-price">
                          {originalPrice.toFixed(2) !== estimatedFare.toFixed(2) && (
                            <div className="vehicle-price-original" style={{ textDecoration: 'line-through', color: '#666' }}>
                              {originalPrice.toFixed(2)} {CURRENCY_SYMBOL}
                            </div>
                          )}
                          {estimatedFare != null
                            ? `${estimatedFare.toFixed(2)} ${CURRENCY_SYMBOL}`
                            : 'Select locations'}
                        </div>
                      </button>
                    );
                  })}
              </div>

              <div className="cb-row-4 passenger-extras">
                <div className="cb-field">
                  <label>Passenger Count</label>
                  <div className="stepper">
                    <button
                      type="button"
                      onClick={() => setPassengers(form.passengers - 1)}
                    >
                      −
                    </button>
                    <span>{form.passengers}</span>
                    <button
                      type="button"
                      onClick={() => setPassengers(form.passengers + 1)}
                    >
                      +
                    </button>

                  </div>
                  {errors.passengers && (
                    <small className="text-danger">{errors.passengers}</small>
                  )}

                </div>

                <div className="cb-field">
                  <label>Small Bag Count</label>
                  <div className="stepper">
                    <button
                      type="button"
                      onClick={() => setSmallBagCount(form.smallBagCount - 1)}
                    >
                      −
                    </button>
                    <span>{form.smallBagCount}</span>
                    <button
                      type="button"
                      onClick={() => setSmallBagCount(form.smallBagCount + 1)}
                    >
                      +
                    </button>

                  </div>
                  {errors.smallBagCount && (
                    <small className="text-danger">{errors.smallBagCount}</small>
                  )}

                </div>

                <div className="cb-field">
                  <label>Medium Bag Count</label>
                  <div className="stepper">
                    <button
                      type="button"
                      onClick={() => setMediumBagCount(form.mediumBagCount - 1)}
                    >
                      −
                    </button>
                    <span>{form.mediumBagCount}</span>
                    <button
                      type="button"
                      onClick={() => setMediumBagCount(form.mediumBagCount + 1)}
                    >
                      +
                    </button>

                  </div>
                  {errors.mediumBagCount && (
                    <small className="text-danger">{errors.mediumBagCount}</small>
                  )}

                </div>

                <div className="cb-field">
                  <label>Large Bag Count</label>
                  <div className="stepper">
                    <button
                      type="button"
                      onClick={() => setLargeBagCount(form.largeBagCount - 1)}
                    >
                      −
                    </button>
                    <span>{form.largeBagCount}</span>
                    <button
                      type="button"
                      onClick={() => setLargeBagCount(form.largeBagCount + 1)}
                    >
                      +
                    </button>

                  </div>
                  {errors.largeBagCount && (
                    <small className="text-danger">{errors.largeBagCount}</small>
                  )}

                </div>

                {/* <div className="cb-field">
                  <label>Extras</label>
                  <div className="extras-row">
                    {EXTRAS.map((extra) => (
                      <button
                        key={extra}
                        type="button"
                        className={`extra-chip${
                          form.extras.includes(extra) ? ' active' : ''
                        }`}
                        onClick={() => toggleExtra(extra)}
                      >
                        {extra}
                      </button>
                    ))}
                  </div>
                </div> */}
              </div>
            </section>

            <section className="cb-card">
              <h3>Special Requests</h3>
              <textarea
                name="specialRequests"
                value={form.specialRequests}
                onChange={handleChange}
                rows={4}
                placeholder="Any special instructions, e.g. meet & greet, child assistance, silent ride..."
              />
            </section>

            <div className="cb-actions">
              <button type="button" className="btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn-confirm" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Confirm Booking →'}
              </button>
            </div>
          </div>

          <aside className="booking-summary">
            <h3>Booking Summary</h3>
            <div className="summary-lines">
              <div>
                <span>Vehicle</span>
                <strong>{summary.vehicle || '—'}</strong>
              </div>
              <div>
                <span>Passengers</span>
                <strong>{summary.passengers}</strong>
              </div>
              <div>
                <span>Trip Type</span>
                <strong>{summary.tripType}</strong>
              </div>
              <div>
                <span>Distance</span>
                <strong>
                  {distanceKm != null ? `${distanceKm.toFixed(2)} km` : '—'}
                </strong>
              </div>
              <div>
                <span>Estimated Fare</span>
                {originalPrice.toFixed(2) !== estimatedFare.toFixed(2) && (
                  <div className="vehicle-price-original" style={{ textDecoration: 'line-through', color: '#666' }}>
                  {Number(originalPrice).toFixed(2)} {CURRENCY_SYMBOL}
                  </div>
                )}
                <strong>{Number(estimatedFare).toFixed(2)} {CURRENCY_SYMBOL}</strong>
              </div>
              
              {/* <div>
                <span>Extras</span>
                <strong>{form.extras.join(', ') || '—'}</strong>
              </div> */}
              <div>
                <span>Small Bag Count</span>
                <strong>{form.smallBagCount}</strong>
              </div>
              <div>
                <span>Medium Bag Count</span>
                <strong>{form.mediumBagCount}</strong>
              </div>
              <div>
                <span>Large Bag Count</span>
                <strong>{form.largeBagCount}</strong>
              </div>
              <div className="commission-line">
                <span>Your Commission ({COMMISSION_RATE * 100}%)</span>
                <strong>{CURRENCY_SYMBOL} {Number(summary.commission).toFixed(2)}</strong>
              </div>
            </div>
            <div className="summary-total">
              <span>Estimated Total</span>
              <strong>{CURRENCY_SYMBOL} {Number(summary.total).toFixed(2)}</strong>
            </div>
            {/* <p className="summary-note">
              Cancellations within 2 hrs of pickup incur a 50% fee. No-shows charged in
              full.
            </p> */}
            {form.tripType === 'Airport' && (

              <div className="cb-card">
                <h3>Airport Details</h3>
                <div className="summary-lines">
                  <div>
                    <span>Airline Name</span>
                    <strong>{flightDetails.airlineName}</strong>
                  </div>
                  <div>
                    <span>Flight Number</span>
                    <strong>{flightDetails.flightNumber}</strong>
                  </div>
                  <div>
                    <span>Airport Name</span>
                    <strong>{flightDetails.airportName}</strong>
                  </div>
                  <div>
                    <span>Terminal</span>
                    <strong>{flightDetails.terminal}</strong>
                  </div>
                  <div>
                    <span>Arrival Date/Time</span>
                    <strong>{flightDetails.arrivalDateTime}</strong>
                  </div>

                </div>
              </div>

            )}
          </aside>


        </form>
      </main>
    </div>
  );
};

export default CreateBooking;
